import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryA from "@/assets/sprinter-executive-1.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryB from "@/assets/sprinter-limo-1.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryC from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/car-service-atlanta-to-chattanooga";
const PAGE_TITLE = "Atlanta to Chattanooga Car Service | Private Sprinter Transfer";
const PAGE_DESC =
  "Chauffeured Mercedes-Benz Sprinter transfers from Atlanta to Chattanooga, Lookout Mountain, and North Georgia. Fixed door-to-door rate. Call (678) 431-7222.";

const faqs = [
  {
    q: "How long does the Atlanta to Chattanooga drive take?",
    a: "About 120 miles and just under two hours on I-75 North outside peak periods. Weekday afternoons out of Atlanta and holiday Fridays through Cartersville and Ringgold add 30 to 45 minutes, and we plan the departure accordingly rather than promising the map's number.",
  },
  {
    q: "Is this quoted hourly or as a flat transfer?",
    a: "A one-way or round-trip transfer is quoted as a fixed rate confirmed in writing. If you want the vehicle waiting between a lunch, a venue tour, and dinner, an hourly as-directed block is usually the better value. Hourly blocks have a 4-hour minimum.",
  },
  {
    q: "Can you handle a corporate group visiting a plant or office up there?",
    a: "Yes, it is one of the most common versions of this route. Teams work through the drive, the chauffeur stays on site for the day, and the group returns together instead of coordinating four rental cars and a parking garage.",
  },
  {
    q: "Do you serve Lookout Mountain and Chickamauga?",
    a: "Yes. Lookout Mountain, Ruby Falls, Rock City, Chickamauga Battlefield, and the Ochs Highway approach are all within the standard service pattern for this route.",
  },
  {
    q: "Can we stop in North Georgia on the way?",
    a: "Frequently. Cartersville, Calhoun, Dalton, and the Blue Ridge turnoff are all reasonable stops. Each is quoted into the trip so the timing and rate stay accurate.",
  },
  {
    q: "What about a Chattanooga Airport pickup?",
    a: "We run the reverse direction regularly, a chauffeur meets your party at Chattanooga Metropolitan (CHA) or a private terminal and drives to Atlanta, which many clients prefer to a short connecting flight.",
  },
];

export const Route = createFileRoute("/car-service-atlanta-to-chattanooga")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "atlanta to chattanooga car service, private transfer atlanta to chattanooga, chattanooga sprinter transfer, lookout mountain car service, north georgia private chauffeur",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Atlanta to Chattanooga Car Service",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Long Distance Private Car Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Chattanooga" },
              { type: "City", name: "Dalton" },
              { type: "City", name: "Cartersville" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Atlanta • I-75 North • Chattanooga"
      h1="Private Car Service from Atlanta to Chattanooga"
      intro="You have a full day ahead in Chattanooga. My chauffeur handles the two-hour drive from Atlanta for your site visit, wedding or weekend, so you can work, talk or rest."
      heroImage={heroImage}
      heroAlt="Executive Mercedes-Benz Sprinter cabin prepared for an Atlanta to Chattanooga transfer"
      midImage={midImage}
      midAlt="Facing captain's chairs inside an Executive Sprinter used on the Chattanooga route"
      sectionOneTitle="Leave the Return Drive to Us"
      sectionOneBody={[
        "After a day at a plant, wedding venue or Lookout Mountain, you still have two hours back through Ringgold and Calhoun. My chauffeur handles that return while your group settles in.",
        "Choose a one-way transfer, round trip or as-directed day with the vehicle staying with you. I'll confirm your fixed rate in writing before you reserve.",
      ]}
      bulletsTitle="How the Chattanooga Route Is Run"
      bulletsIntro="One chauffeur and vehicle, with the schedule planned around your day."
      bullets={[
        "Fixed round-trip or one-way rate confirmed in writing",
        "Pickup from any metro Atlanta address, office, hotel, or FBO",
        "Departure timed backwards from your first appointment, with the I-75 buffer built in",
        "Chauffeur remains on site through a corporate day or venue tour",
        "Stops in Cartersville, Calhoun, Dalton, or Blue Ridge priced into the quote",
        "Lookout Mountain, Rock City, Ruby Falls, and Chickamauga access",
        "Reverse transfers from Chattanooga Metropolitan Airport (CHA) to Atlanta",
      ]}
      coverageTitle="Destinations on and Around This Route"
      coverageIntro="Where the I-75 North corridor actually takes our clients."
      coverage={[
        "Downtown Chattanooga",
        "Chattanooga Riverfront & Bluff View",
        "Lookout Mountain",
        "Rock City & Ruby Falls",
        "Chickamauga Battlefield",
        "Chattanooga Metropolitan Airport (CHA)",
        "Signal Mountain",
        "Dalton & Calhoun",
        "Cartersville & Adairsville",
        "Ringgold & Fort Oglethorpe",
        "Blue Ridge turnoff",
        "Ellijay & the North Georgia mountains",
      ]}
      routeTable={{
        title: "Drive Times North of Atlanta",
        intro:
          "Planning figures from in-town Atlanta. Northbound I-75 is predictable except on Friday afternoons and holiday weekends, when we add a deliberate buffer.",
        rows: [
          {
            route: "Atlanta → Downtown Chattanooga",
            distance: "~120 mi",
            time: "1h 50m – 2h 35m",
            notes: "I-75 N straight through. Ringgold truck volume is the usual delay.",
          },
          {
            route: "Atlanta → Lookout Mountain",
            distance: "~125 mi",
            time: "2h 05m – 2h 45m",
            notes: "Ochs Highway ascent; we allow extra time in winter weather.",
          },
          {
            route: "Atlanta → Chattanooga Airport (CHA)",
            distance: "~124 mi",
            time: "1h 55m – 2h 35m",
            notes: "Common alternative when an Atlanta connection collapses.",
          },
          {
            route: "Atlanta → Dalton",
            distance: "~90 mi",
            time: "1h 25m – 1h 55m",
            notes: "Frequent corporate stop on the way north; often paired with a Chattanooga day.",
          },
          {
            route: "Atlanta → Cartersville",
            distance: "~45 mi",
            time: "45m – 1h 15m",
            notes: "Booth Museum and venue traffic; easy add-on stop on the route.",
          },
          {
            route: "Atlanta → Blue Ridge",
            distance: "~95 mi",
            time: "1h 45m – 2h 15m",
            notes: "GA-515 after Jasper. Two-lane sections slow considerably in fall leaf season.",
          },
          {
            route: "Chattanooga as-directed day",
            distance: "Varies",
            time: "8–10 hr block",
            notes: "Vehicle and chauffeur stay with the group across site visits and dinner.",
          },
        ],
        footnote:
          "Round-trip quotes include waiting time on site, the chauffeur does not leave and re-bill for the return.",
      }}
      detailSections={[
        {
          title: "Corporate Site Visits and Plant Tours",
          body: [
            "The Atlanta-to-Chattanooga corridor is heavy with manufacturing, logistics, and flooring industry travel, and the same pattern repeats: a team of six to ten leaves Atlanta at 6:30 a.m., needs to be presentable at 9, and cannot arrive in three separate cars at three separate times.",
            "We collect everyone from one office or hotel, run the drive as usable time, hold the vehicle on site for the day, and bring the group back together. Corporate accounts can put the whole thing on consolidated monthly billing.",
          ],
        },
        {
          title: "Weddings, Weekends, and Mountain Trips",
          list: [
            "Chattanooga and Lookout Mountain wedding venues, with guest transfers from the hotel block.",
            "Fall leaf-season trips through Blue Ridge and Ellijay before continuing north.",
            "Riverfront and Bluff View dinner evenings with the chauffeur waiting between stops.",
            "Family groups avoiding two rental cars and mountain-road driving after dark.",
          ],
        },
        {
          title: "Choosing the Vehicle",
          list: [
            "2–4 passengers, Suburban SUV, best for a fast executive turn-and-back.",
            "Up to 13 passengers, Executive Sprinter with facing captain's chairs and worktable space.",
            "9–14 passengers, Mercedes-Benz Sprinter Limousine with lounge seating and wet bar.",
            "15+ passengers, two Sprinters travelling together under one lead chauffeur.",
          ],
        },
      ]}
      gallery={{
        title: "Vehicles on the Chattanooga Route",
        intro: "Cabins that make a two-hour each-way day comfortable rather than costly.",
        images: [
          { src: galleryA, alt: "Executive Mercedes-Benz Sprinter interior for corporate travel to Chattanooga", caption: "Executive Sprinter, up to 13" },
          { src: galleryB, alt: "Mercedes-Benz Sprinter Limousine lounge cabin for group trips to Chattanooga", caption: "Sprinter Limousine, up to 14" },
          { src: galleryC, alt: "Suburban SUV interior for small-group Atlanta to Chattanooga transfers", caption: "Suburban SUV, up to 6 (captain seats) or 7 (bench seats)" },
        ],
      }}

      faqs={faqs}
      faqTitle="Atlanta to Chattanooga Car Service FAQs"
      relatedTitle="Related Destination Services"
      related={[
        { to: "/car-service-atlanta-to-savannah", title: "Atlanta to Savannah", desc: "Coastal Georgia transfers, door to door." },
        { to: "/car-service-atlanta-to-lake-oconee", title: "Atlanta to Lake Oconee", desc: "Reynolds and Ritz-Carlton weekends." },
        { to: "/corporate-transportation-atlanta", title: "Corporate Transportation", desc: "Dedicated accounts with monthly billing." },
      ]}
      ctaTitle="Text Me Your Chattanooga Plans"
      ctaBody="Text me your date, pickup, group size and stops. Tell me whether it's one way, a round trip or a full day, and I'll send your quote in writing."
    />
  );
}
